import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "../mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/";
  // Solid (dark) navbar whenever scrolled OR on any non-home page, so the
  // white logo/links stay visible over light-background pages.
  const solid = scrolled || !isHome;

  useEffect(() => {
    const ids = NAV_LINKS.filter((l) => l.href.startsWith("#")).map((l) =>
      l.href.replace("#", "")
    );
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const probe = window.scrollY + 140;
      let current = "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= probe) current = id;
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Navigate to a section (hash) or a route (path).
  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    // hash anchor pointing at a home-page section
    if (!isHome) {
      navigate("/" + href); // e.g. "/#film" -> ScrollManager scrolls to it
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const isLinkActive = (href) =>
    href.startsWith("/")
      ? location.pathname === href
      : isHome && active === href.replace("#", "");

  // Real, deep-linkable href for each nav item so the address bar reflects it.
  const hrefFor = (href) =>
    href.startsWith("/") ? href : isHome ? href : "/" + href;

  const onNavClick = (e, href) => {
    e.preventDefault();
    go(href);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-[#1C2621]/90 backdrop-blur-md py-2 shadow-lg shadow-black/10"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => go("/")} className="flex items-center group">
          <img
            src="/siq-logo.png"
            alt="SIQ Adventures"
            className={`w-auto transition-all duration-500 group-hover:scale-105 ${
              solid ? "h-20" : "h-24 md:h-28"
            }`}
          />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {NAV_LINKS.map((l) => {
            const isActive = isLinkActive(l.href);
            return (
              <a
                key={l.label}
                href={hrefFor(l.href)}
                onClick={(e) => onNavClick(e, l.href)}
                className={`text-[15px] cursor-pointer transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:bg-[#D2703F] after:transition-all after:duration-300 ${
                  isActive
                    ? "text-[#F4EFE6] after:w-full"
                    : "text-[#F4EFE6]/85 hover:text-[#F4EFE6] after:w-0 hover:after:w-full"
                }`}
              >
                {l.label}
              </a>
            );
          })}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <button
            onClick={() => go("#contact")}
            className="group inline-flex items-center gap-2 rounded-full bg-[#D2703F] hover:bg-[#B85A2C] text-white text-[15px] font-medium px-6 py-2.5 transition-all duration-300 shadow-md shadow-[#D2703F]/30 hover:shadow-lg hover:-translate-y-0.5"
          >
            Plan Your Trip
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-[#F4EFE6] p-1"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-[#1C2621]/97 backdrop-blur-md ${
          open ? "max-h-[430px] mt-3" : "max-h-0"
        }`}
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {NAV_LINKS.map((l) => (
            <a
              key={l.label}
              href={hrefFor(l.href)}
              onClick={(e) => onNavClick(e, l.href)}
              className="text-left text-[#F4EFE6]/90 text-lg py-3 border-b border-white/10 cursor-pointer"
            >
              {l.label}
            </a>
          ))}
          <a
            href={isHome ? "#contact" : "/#contact"}
            onClick={(e) => onNavClick(e, "#contact")}
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-[#D2703F] text-white font-medium px-6 py-3 cursor-pointer"
          >
            Plan Your Trip <ArrowRight className="w-4 h-4" />
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
